import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import { Info } from '@lifeomic/chromicons';
import { Link } from '@lifeomic/chroma-react/components/Link';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { TableModule } from '@lifeomic/chroma-react/components/TableModule';
import { Tooltip } from '@lifeomic/chroma-react/components/Tooltip';
import React from 'react';
import EmptyPageState from '@lifeomic/phc-web-toolkit/src/components/EmptyPageState';
import { DNAIcon } from '../../../../src/components/common/icons';
import urls from '../../../../src/util/urls';
import { useActiveAccountId } from '@lifeomic/phc-web-toolkit/src/hooks';
import { getAminoAcidField } from '../utils';

const messages = defineMessages({
  noTranscripts: {
    id: 'omicsExplorer.variantDetails.dialog.noTranscripts',
    defaultMessage: 'No alterante transcripts',
  },
});

const useStyles = makeStyles(theme => ({
  root: {},
  marginRight: {
    marginRight: theme.spacing(1),
  },
  icon: {
    color: theme.palette.blue.main,
    height: theme.pxToRem(15),
    width: theme.pxToRem(15),
  },
}));

const getTableConfig = ({ classes, ensemblCanonVariant, aminoAcidPath }) => [
  {
    header: {
      label: (
        <FormattedMessage
          id="omicsExplorer.variantDetails.dialog.alternateTranscripts.transcriptId"
          defaultMessage="Transcript ID"
        />
      ),
    },
    cell: {
      content: value => {
        const { transcriptId } = value;

        if (!transcriptId) {
          return null;
        }

        const matchesCanonical =
          ensemblCanonVariant &&
          ensemblCanonVariant.transcriptId === value.transcriptId;

        return (
          <React.Fragment>
            <Link
              className={matchesCanonical && classes.marginRight}
              to={urls.ensemble.gene(transcriptId)}
              newTab
            >
              {transcriptId}
            </Link>
            {matchesCanonical && (
              <Tooltip
                title={
                  <FormattedMessage
                    id="omicsExplorer.variantDetails.dialog.alternateTranscripts.primaryTranscript"
                    defaultMessage="This is the primary transcript"
                  />
                }
              >
                <span>
                  <Info className={classes.icon} />
                </span>
              </Tooltip>
            )}
          </React.Fragment>
        );
      },
    },
  },
  {
    header: {
      label: (
        <FormattedMessage
          id="omicsExplorer.variantDetails.dialog.alternateTranscripts.nnChange"
          defaultMessage="NC Change"
        />
      ),
    },
    cell: {
      valuePath: 'nucleotideChange',
    },
  },
  {
    header: {
      label: (
        <FormattedMessage
          id="omicsExplorer.variantDetails.dialog.alternateTranscripts.aaChange"
          defaultMessage="AA Change"
        />
      ),
    },
    cell: {
      valuePath: aminoAcidPath,
    },
  },
  {
    header: {
      label: (
        <FormattedMessage
          id="omicsExplorer.variantDetails.dialog.alternateTranscripts.variantClass"
          defaultMessage="Variant Class"
        />
      ),
    },
    cell: {
      valuePath: 'class',
    },
  },
  {
    header: {
      label: (
        <FormattedMessage
          id="omicsExplorer.variantDetails.dialog.alternateTranscripts.geneClass"
          defaultMessage="Gene Class"
        />
      ),
    },
    cell: {
      valuePath: 'biotype',
    },
  },
  {
    header: {
      label: (
        <FormattedMessage
          id="omicsExplorer.variantDetails.dialog.alternateTranscripts.exonNumber"
          defaultMessage="EXON Number"
        />
      ),
    },
    cell: {
      valuePath: 'exonIntronRank',
    },
  },
];

export const AlternateTranscripts = ({
  contentClassName,
  data,
  emptyStateClassName,
}) => {
  const { formatMessage } = useIntl();
  const classes = useStyles({});
  const activeAccount = useActiveAccountId();
  const aminoAcidPath = getAminoAcidField(activeAccount);

  if (!data.ensembl) {
    return (
      <div className={emptyStateClassName}>
        <EmptyPageState
          message={formatMessage(messages.noTranscripts)}
          illustration={DNAIcon}
        />
      </div>
    );
  }

  const ensemblCanonVariant = data.ensemblCanon && data.ensemblCanon[0];

  return (
    <div className={contentClassName}>
      <TableModule
        config={getTableConfig({ classes, ensemblCanonVariant, aminoAcidPath })}
        data={data.ensembl}
      />
    </div>
  );
};
